import {
  buildHourlyTrendBuckets,
  createdAtDayKey,
  createdAtHourKey,
  formatChartAxisLabel,
  formatTrendPeriodTooltip,
  getChartAxisEndpointLabels,
  localHourKeyFromDate,
  type TrendBucketMode,
} from './connectionDashboard';
import type {
  SiteAnalyticsChartPeriodHours,
  SiteAnalyticsTrendBucketMode,
  SiteAnalyticsTrendPoint,
} from './posthogSiteActivity';

export type SiteTrafficTrendPoint = {
  label: string;
  periodStart: string;
  anonymousViews: number;
  knownViews: number;
  isFuture: boolean;
  /** True at the top of each hour — used for darker chart grid lines. */
  isHourMark: boolean;
};

type TrendBucketSpec = {
  mode: SiteAnalyticsTrendBucketMode;
  intervalMinutes: number;
};

function startOfLocalDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function localDayKeyFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDayLabel(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function isMonday(isoDate: string): boolean {
  return new Date(`${isoDate}T12:00:00`).getDay() === 1;
}

function startOfLocalHour(d: Date): Date {
  const copy = new Date(d);
  copy.setMinutes(0, 0, 0);
  return copy;
}

function startOfLocalMinuteInterval(d: Date, intervalMinutes: number): Date {
  const copy = startOfLocalHour(d);
  const flooredMinutes = Math.floor(d.getMinutes() / intervalMinutes) * intervalMinutes;
  copy.setMinutes(flooredMinutes);
  return copy;
}

function startOfLocalSixHour(d: Date): Date {
  const copy = startOfLocalHour(d);
  copy.setHours(Math.floor(copy.getHours() / 6) * 6);
  return copy;
}

function localMinuteIntervalKeyFromDate(d: Date, intervalMinutes: number): string {
  const bucket = startOfLocalMinuteInterval(d, intervalMinutes);
  const y = bucket.getFullYear();
  const m = String(bucket.getMonth() + 1).padStart(2, '0');
  const day = String(bucket.getDate()).padStart(2, '0');
  const hour = String(bucket.getHours()).padStart(2, '0');
  const minute = String(bucket.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${hour}:${minute}`;
}

function localSixHourKeyFromDate(d: Date): string {
  return localHourKeyFromDate(startOfLocalSixHour(d));
}

function formatMinuteIntervalLabel(key: string, intervalMinutes: number): string {
  const match = key.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return key;
  const [, y, mo, day, hour, minute] = match;
  const at = new Date(Number(y), Number(mo) - 1, Number(day), Number(hour), Number(minute));
  if (intervalMinutes >= 60) {
    return at.toLocaleTimeString(undefined, { hour: 'numeric' });
  }
  return at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatSixHourLabel(key: string): string {
  const match = key.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2})$/);
  if (!match) return key;
  const [, y, mo, day, hour] = match;
  const at = new Date(Number(y), Number(mo) - 1, Number(day), Number(hour));
  return at.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
  });
}

function formatMinuteIntervalTooltip(key: string): string {
  const match = key.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return key;
  const [, y, mo, day, hour, minute] = match;
  const at = new Date(Number(y), Number(mo) - 1, Number(day), Number(hour), Number(minute));
  return at.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatSixHourTooltip(key: string): string {
  const match = key.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2})$/);
  if (!match) return key;
  const [, y, mo, day, hour] = match;
  const at = new Date(Number(y), Number(mo) - 1, Number(day), Number(hour));
  return at.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
  });
}

export function siteAnalyticsTrendBucketSpec(
  trendPeriodHours: SiteAnalyticsChartPeriodHours,
): TrendBucketSpec {
  switch (trendPeriodHours) {
    case 1:
    case 2:
    case 6:
      return { mode: '5min', intervalMinutes: 5 };
    case 12:
      return { mode: '30min', intervalMinutes: 30 };
    case 48:
      return { mode: 'hourly', intervalMinutes: 60 };
    case 168:
      return { mode: '6hour', intervalMinutes: 360 };
    case 360:
    case 720:
    case 1440:
      return { mode: 'daily', intervalMinutes: 1440 };
  }
}

export function siteAnalyticsTrendBucketMode(
  trendPeriodHours: SiteAnalyticsChartPeriodHours,
): SiteAnalyticsTrendBucketMode {
  return siteAnalyticsTrendBucketSpec(trendPeriodHours).mode;
}

/** Maps site bucket modes to connectionDashboard axis formatters. */
export function siteAnalyticsChartBucketMode(
  trendPeriodHours: SiteAnalyticsChartPeriodHours,
): TrendBucketMode {
  const mode = siteAnalyticsTrendBucketMode(trendPeriodHours);
  if (mode === '5min' || mode === '30min') return '5min';
  if (mode === 'daily') return 'daily';
  return 'hourly';
}

export function siteAnalyticsTrendUnitLabel(
  trendPeriodHours: SiteAnalyticsChartPeriodHours,
): string {
  const mode = siteAnalyticsTrendBucketMode(trendPeriodHours);
  if (mode === '5min') return '5 min';
  if (mode === '30min') return '30 min';
  if (mode === '6hour') return '6 hr';
  if (mode === 'daily') return 'Days';
  return 'Hours';
}

export function formatSiteTrendPeriodTooltip(
  periodStart: string,
  trendPeriodHours: SiteAnalyticsChartPeriodHours,
): string {
  const mode = siteAnalyticsTrendBucketMode(trendPeriodHours);
  if (mode === '5min' || mode === '30min') return formatMinuteIntervalTooltip(periodStart);
  if (mode === '6hour') return formatSixHourTooltip(periodStart);
  if (mode === 'daily') return formatTrendPeriodTooltip(periodStart, 'daily');
  return formatTrendPeriodTooltip(periodStart, 'hourly');
}

export function getSiteTrendChartAxisLabels(
  points: SiteTrafficTrendPoint[],
  trendPeriodHours: SiteAnalyticsChartPeriodHours,
): { start: string; end: string } {
  const realPoints = points.filter((point) => !point.isFuture);
  const startPoint = realPoints[0] ?? points[0];
  const endPoint = realPoints[realPoints.length - 1] ?? points[points.length - 1];
  if (!startPoint || !endPoint) return { start: '', end: '' };

  const mode = siteAnalyticsTrendBucketMode(trendPeriodHours);
  const spec = siteAnalyticsTrendBucketSpec(trendPeriodHours);

  if (mode === '5min' || mode === '30min') {
    return {
      start: formatMinuteIntervalLabel(startPoint.periodStart, spec.intervalMinutes),
      end: formatMinuteIntervalLabel(endPoint.periodStart, spec.intervalMinutes),
    };
  }
  if (mode === '6hour') {
    return {
      start: formatSixHourLabel(startPoint.periodStart),
      end: formatSixHourLabel(endPoint.periodStart),
    };
  }
  if (mode === 'daily') {
    return getChartAxisEndpointLabels(points, 'daily');
  }

  return getChartAxisEndpointLabels(points, 'hourly');
}

function trendKeyFromRawPeriodStart(
  periodStart: string,
  bucketMode: SiteAnalyticsTrendBucketMode,
  intervalMinutes: number,
): string {
  const parsed = new Date(periodStart);
  if (Number.isNaN(parsed.getTime())) return '';

  switch (bucketMode) {
    case '5min':
    case '30min':
      return localMinuteIntervalKeyFromDate(parsed, intervalMinutes);
    case 'hourly':
      return createdAtHourKey(periodStart) || localHourKeyFromDate(parsed);
    case '6hour':
      return localSixHourKeyFromDate(parsed);
    case 'daily':
      return createdAtDayKey(periodStart);
    default:
      return '';
  }
}

function isHourMarkKey(key: string): boolean {
  return /T\d{2}:00$/.test(key);
}

function buildMinuteIntervalTrendBuckets(
  periodHours: number,
  intervalMinutes: number,
): { key: string; label: string; isFuture: boolean; isHourMark: boolean }[] {
  const now = new Date();
  const currentStart = startOfLocalMinuteInterval(now, intervalMinutes);
  const currentKey = localMinuteIntervalKeyFromDate(currentStart, intervalMinutes);
  const bucketCount = Math.ceil((periodHours * 60) / intervalMinutes);
  const intervalMs = intervalMinutes * 60 * 1000;
  const buckets: { key: string; label: string; isFuture: boolean; isHourMark: boolean }[] = [];

  for (let i = bucketCount - 1; i >= -1; i--) {
    const d = new Date(currentStart.getTime() - i * intervalMs);
    const key = localMinuteIntervalKeyFromDate(d, intervalMinutes);
    buckets.push({
      key,
      label: formatMinuteIntervalLabel(key, intervalMinutes),
      isFuture: key > currentKey,
      isHourMark: isHourMarkKey(key),
    });
  }

  return buckets;
}

function buildSixHourTrendBuckets(periodHours: number): { key: string; label: string; isFuture: boolean; isHourMark: boolean }[] {
  const now = new Date();
  const currentStart = startOfLocalSixHour(now);
  const currentKey = localSixHourKeyFromDate(currentStart);
  const bucketCount = Math.ceil(periodHours / 6);
  const intervalMs = 6 * 60 * 60 * 1000;
  const buckets: { key: string; label: string; isFuture: boolean; isHourMark: boolean }[] = [];

  for (let i = bucketCount - 1; i >= -1; i--) {
    const d = new Date(currentStart.getTime() - i * intervalMs);
    const key = localSixHourKeyFromDate(d);
    buckets.push({
      key,
      label: formatSixHourLabel(key),
      isFuture: key > currentKey,
      isHourMark: true,
    });
  }

  return buckets;
}

function buildDailyTrendBuckets(
  periodHours: number,
): { key: string; label: string; isFuture: boolean; isHourMark: boolean }[] {
  const now = new Date();
  const todayKey = localDayKeyFromDate(startOfLocalDay(now));
  const dayCount = Math.ceil(periodHours / 24);
  const buckets: { key: string; label: string; isFuture: boolean; isHourMark: boolean }[] = [];

  for (let i = dayCount - 1; i >= -1; i--) {
    const d = startOfLocalDay(now);
    d.setDate(d.getDate() - i);
    const key = localDayKeyFromDate(d);
    buckets.push({
      key,
      label: formatDayLabel(key),
      isFuture: key > todayKey,
      isHourMark: isMonday(key),
    });
  }

  return buckets;
}

function buildTrendBuckets(
  trendPeriodHours: SiteAnalyticsChartPeriodHours,
  spec: TrendBucketSpec,
): { key: string; label: string; isFuture: boolean; isHourMark: boolean }[] {
  switch (spec.mode) {
    case '5min':
    case '30min':
      return buildMinuteIntervalTrendBuckets(trendPeriodHours, spec.intervalMinutes);
    case 'hourly':
      return buildHourlyTrendBuckets(trendPeriodHours).map((b) => ({
        key: b.key,
        label: b.label,
        isFuture: b.isFuture,
        isHourMark: b.isDivider,
      }));
    case '6hour':
      return buildSixHourTrendBuckets(trendPeriodHours);
    case 'daily':
      return buildDailyTrendBuckets(trendPeriodHours);
    default:
      return [];
  }
}

export function buildSiteTrafficTrend(
  rawTrend: SiteAnalyticsTrendPoint[],
  trendPeriodHours: SiteAnalyticsChartPeriodHours,
  bucketMode: SiteAnalyticsTrendBucketMode = siteAnalyticsTrendBucketMode(trendPeriodHours),
): SiteTrafficTrendPoint[] {
  const spec = siteAnalyticsTrendBucketSpec(trendPeriodHours);
  const buckets = buildTrendBuckets(trendPeriodHours, spec);

  const counts = new Map<string, { anonymousViews: number; knownViews: number }>();
  for (const row of rawTrend) {
    const key = trendKeyFromRawPeriodStart(row.periodStart, bucketMode, spec.intervalMinutes);
    if (!key) continue;
    const existing = counts.get(key);
    if (existing) {
      existing.anonymousViews += row.anonymousViews;
      existing.knownViews += row.knownViews;
    } else {
      counts.set(key, {
        anonymousViews: row.anonymousViews,
        knownViews: row.knownViews,
      });
    }
  }

  return buckets.map((bucket) => {
    const hit = counts.get(bucket.key);
    return {
      label: bucket.label,
      periodStart: bucket.key,
      anonymousViews: hit?.anonymousViews ?? 0,
      knownViews: hit?.knownViews ?? 0,
      isFuture: bucket.isFuture,
      isHourMark: bucket.isHourMark,
    };
  });
}

/** For axis formatting when using connectionDashboard helpers directly. */
export function formatSiteTrendAxisLabel(
  periodStart: string,
  trendPeriodHours: SiteAnalyticsChartPeriodHours,
): string {
  const mode = siteAnalyticsTrendBucketMode(trendPeriodHours);
  const spec = siteAnalyticsTrendBucketSpec(trendPeriodHours);
  if (mode === '5min' || mode === '30min') {
    return formatMinuteIntervalLabel(periodStart, spec.intervalMinutes);
  }
  if (mode === '6hour') return formatSixHourLabel(periodStart);
  if (mode === 'daily') return formatDayLabel(periodStart);
  return formatChartAxisLabel(periodStart, 'hourly');
}
